// Describes a non-Error unhandled rejection for Sentry by shape only (type, constructor, own keys, event type and
// target, a CustomEvent's `detail` shape), never values, so nothing it carries reaches telemetry; target URLs keep
// only origin and path.

import type { ErrorEvent } from '@sentry/browser'

const MAX_KEYS = 10
const REJECTION_MECHANISM_SUFFIX = 'onunhandledrejection'

export type RejectionShape = {
  route: string
  kind: string
  constructor_name?: string
  keys?: string[]
  event_type?: string
  event_target?: string
  detail_kind?: string
  detail_keys?: string[]
}

const ownKeys = (value: object): string[] => Object.keys(value).slice(0, MAX_KEYS)

const kindOf = (value: unknown): string => (value === null ? 'null' : typeof value)

const urlWithoutQuery = (url: string): string => {
  try {
    const parsed = new URL(url)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return ''
  }
}

const describeTarget = (target: EventTarget | null): string | undefined => {
  if (!(target instanceof Element)) return target ? kindOf(target) : undefined
  const source = target.getAttribute('src') ?? target.getAttribute('href')
  const tag = target.tagName.toLowerCase()
  return source ? `${tag} ${urlWithoutQuery(new URL(source, document.baseURI).href)}` : tag
}

const describeEvent = (event: Event): Partial<RejectionShape> => {
  const detail = event instanceof CustomEvent ? (event.detail as unknown) : undefined
  return {
    event_type: event.type,
    event_target: describeTarget(event.target),
    ...(event instanceof CustomEvent && {
      detail_kind: kindOf(detail),
      ...(detail !== null && typeof detail === 'object' && { detail_keys: ownKeys(detail) })
    })
  }
}

/** The shape of a non-Error rejection value and the page it happened on, without any of its values. */
export const describeRejection = (reason: unknown, route: string): RejectionShape => {
  const shape: RejectionShape = { route, kind: kindOf(reason) }
  if (reason === null || typeof reason !== 'object') return shape
  return {
    ...shape,
    constructor_name: (reason as { constructor?: { name?: string } }).constructor?.name,
    keys: ownKeys(reason),
    ...(reason instanceof Event && describeEvent(reason))
  }
}

/** The event with a `rejection_shape` context when it reports an unhandled rejection whose value is not an Error. */
export const withRejectionShape = (event: ErrorEvent, originalException: unknown, route: string): ErrorEvent => {
  const isRejection = event.exception?.values?.[0]?.mechanism?.type?.endsWith(REJECTION_MECHANISM_SUFFIX) === true
  if (!isRejection || originalException instanceof Error) return event
  return { ...event, contexts: { ...event.contexts, rejection_shape: describeRejection(originalException, route) } }
}
